import { mapState, mapGetters } from 'vuex'
// Relative imports (not the `@/` alias): the pre-commit hook lints newly ADDED
// files with `import/no-unresolved` as an error and no alias resolver.
import Config from '../config'
import { canAccessProfile } from '../utils/profile-access.utils'
import { getActiveTabByRouteName, ACTIVE_TAB } from '../utils/active-tab.utils'
import util from '../util'

/**
 * The app-navigation items that used to render as the top tab bar
 * (`components/Tabs.vue`) and render as rows inside the hamburger drawer
 * (`components/UtilityMenu.vue`) while the hamburger flag is ON.
 *
 * Every visibility condition, route target, disabled rule and the GA4
 * `Main screen > Switch tab` event are carried over unchanged — only the
 * presentation layer moved.
 */
export default {
  computed: {
    ...mapGetters('zoomApp', ['isZoomApp']),
    ...mapGetters(['isEnableSlideReview']),
    ...mapState({
      currentUser: (state) => state.currentUser,
      activeTabView: (state) => state.activeTabView,
      presentation: (state) => state.presentation,
      audienceId: (state) => state.audienceId,
    }),
    isSkoleTube() {
      return this.presentation
        && this.presentation.user
        && this.presentation.user.identifyProvider === 'skoletube'
    },
    isTabReviewDisabled() {
      return !this.audienceId
    },
    isTabFeedbackDisabled() {
      return !this.audienceId
    },
    isShowTabFeedback() {
      return (this.presentation && this.presentation.isEnablePrivateFeedback) || this.isTabFeedbackDisabled
    },
    isShowTabProfile() {
      return canAccessProfile({
        presentation: this.presentation,
        isSkoleTube: this.isSkoleTube,
        isZoomApp: this.isZoomApp,
      })
    },
    isEmbedAudienceApp() {
      return window.location.href.includes(Config.presenterUrl)
    },
    isShowTabZoomLogout() {
      return this.isZoomApp && this.isEmbedAudienceApp
    },
    // Route target of the Presentation / My account rows depends on whether we
    // are inside a presentation or on the standalone home/account routes.
    presentationRouteName() {
      return this.presentation && this.presentation.uniqueAccessCode ? 'presentation' : 'home'
    },
    profileRouteName() {
      return this.presentation && this.presentation.uniqueAccessCode ? 'profile' : 'account'
    },
  },
  methods: {
    isActiveTab(tab) {
      return this.activeTabView === tab
    },
    trackingSwitchTab(value) {
      const activeTab = getActiveTabByRouteName(value)

      if (activeTab === ACTIVE_TAB.PROFILE && this.currentUser && this.currentUser.id) {
        this.$store.state.lazyTrackingSwitchTab = true

        return
      }

      const mappingTabEventLabel = {
        feedback: 'Feedback',
        profile: 'Account',
        slide: 'Presentation',
        lookback: 'Lookback',
      }

      const trackingProps = {
        eventCategory: 'Audience',
        eventLabel: mappingTabEventLabel[activeTab],
        presentationId: this.presentation && this.presentation.id,
      }

      util.ga4Track('Main screen > Switch tab', trackingProps)
    },
    handleSwitchTab(routeName, isTabDisabled = false) {
      const isTheSameTab = getActiveTabByRouteName(routeName) === this.activeTabView

      if (isTabDisabled || isTheSameTab) return

      this.$router.push({
        name: routeName,
        params: {
          id: this.$route.params.id,
        },
      })

      this.trackingSwitchTab(routeName)
    },
    handleLogoutZoomApp() {
      util.ga4Track('Main screen > Zoom log out', {
        eventCategory: 'Audience',
        presentationId: this.presentation && this.presentation.id,
      })

      window.parent.postMessage('logoutZoomApp', Config.presenterUrl)
    },
  },
}
